import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, Popconfirm, Form, message, Row, Col } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import AntDesignTable from "../common/antTable/AntDesignTable";
import CommonModal from "../common/commonModal/CommonModal";
import { API_URL } from "../../config";
import PatientForm from "./PatientForm";
import moment from "moment";
import PatientDetails from "../patientsComponents/PatientDetails";
import { useTranslation } from "react-i18next";

const PatientsContainer = () => {

  const { t } = useTranslation();

  const [patients, setPatients] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPatient, setCurrentPatient] = useState(null);
  const [form] = Form.useForm();
  const [selectedPatient, setSelectedPatient] = useState(null);

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/?skip=0&limit=100`);
      setPatients(response.data);
    } catch (error) {
      console.error("There was an error fetching the patient data!", error);
    }
  };

  const fetchPatientDetails = async (patient_id) => {
    try {
      const response = await axios.get(`${API_URL}/user/${patient_id}`);
      const patientData = response.data;
      patientData.date_of_birth = moment(patientData.date_of_birth);
      setCurrentPatient(patientData);
      form.setFieldsValue(patientData);
    } catch (error) {
      console.error("There was an error fetching the patient details!", error);
    }
  };

  const handleEdit = async (record) => {
    await fetchPatientDetails(record.patient_id);
    setIsModalVisible(true);
  };

  const handleDelete = async (patient_id) => {
    try {
      await axios.delete(`${API_URL}/user/${patient_id}`);
      message.success(t("message.patientDelete"));
      fetchPatients();
    } catch (error) {
      console.error("There was an error deleting the patient data!", error);
      message.error("Failed to delete the patient");
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const updatedPatient = {
        ...values,
        date_of_birth: values.date_of_birth.format("YYYY-MM-DD"),
      };

      await axios.patch(
        `${API_URL}/user/${currentPatient.patient_id}`,
        updatedPatient,
      );
      message.success(t("message.patientUpdate"))
      setIsModalVisible(false);
      fetchPatients();
    } catch (error) {
      console.error("There was an error updating the patient data!", error);
      message.error("Failed to update the patient");
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const columns = [
    {
      title: t('tableHeader.name'),
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <a onClick={() => handleDetails(record)}>
          {record.first_name} {record.last_name}
        </a>
      ),
    },
    {
      title: t('tableHeader.gender'),
      dataIndex: "gender",
      key: "gender",
    },
    {
      title: t('tableHeader.age'),
      dataIndex: "age",
      key: "age",
      render: (_, record) => moment().diff(record.date_of_birth, "years"),
    },
    {
      title: t('tableHeader.phoneNumber'),
      dataIndex: "phone_number",
      key: "phone_number",
    },
    {
      title: t('tableHeader.email'),
      dataIndex: "email_address",
      key: "email_address",
    },

    {
      title: t('tableHeader.action'),
      key: "action",
      render: (text, record) => (
        <span>
          <Row gutter={[8, 8]}>
            <Col xs={12} sm={8} md={6}>
              <Button
                className="edit-button"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Col>

            <Col xs={12} sm={8} md={6}>
              <Popconfirm
                title={t("message.patientDeleteConfirmation")}
                onConfirm={() => handleDelete(record.patient_id)}
                okText={t("common.yes")}
                cancelText={t("common.no")}
              >
                <Button
                  className="delete-button"
                  icon={<DeleteOutlined />}
                  style={{ marginLeft: 8 }}
                />
              </Popconfirm>
            </Col>
          </Row>
        </span>
      ),
    },
  ];

  const handleDetails = (record) => {
    setSelectedPatient(record);
  };

  return (
    <React.Fragment>
      {selectedPatient ? (
        <PatientDetails patient={selectedPatient} />
      ) : (
        <>
          <AntDesignTable columns={columns} tableData={patients} />
          <CommonModal
            title={t("patientPage.editPatient")}
            visible={isModalVisible}
            onCancel={handleCancel}
            onConfirm={handleOk}
            confirmButtonText={t("common.submit")}
            width={1000}
          >
            <PatientForm form={form} formData={currentPatient} />
          </CommonModal>
        </>
      )}
    </React.Fragment>
  );
};

export default PatientsContainer;
